import { css, EllipsisText, Mode, styled, TextInput } from 'canoe';
import React, { FocusEventHandler, KeyboardEventHandler } from 'react';

import Theme from 'canoe/types/theme';

import { ValueBoxConsumer } from '../ValueBox';

const BaseInput = styled(TextInput)<{
  hasError?: boolean;
  hasWarning?: boolean;
  prefix?: string;
  variant?: 'default' | 'collections';
}>`
  ${({ theme, hasError, prefix, variant, hasWarning }) => css`
    height: 32px;
    padding: 0 ${theme.spaces.xs};
    font-size: ${theme.fonts.small.fontSize};
    border-color: ${theme.colors.GlitterGray};
    box-shadow: none;

    &:hover,
    &:focus {
      box-shadow: none;
      border-color: ${theme.colors.GlitterGrayD20};
    }

    ${
      prefix &&
      css`
        padding-left: ${theme.spaces.xxs};

        + label {
          height: 32px;
          font-size: ${theme.fonts.small.fontSize};
          padding: ${theme.spaces.xxs} ${theme.spaces.xs} ${theme.spaces.xxs} ${theme.spaces.xs};
        }
      `
    }

    ${
      variant !== 'collections' &&
      css`
        ${prefix &&
        css`
          + label {
            font-weight: ${theme.fontWeights.normal};
            background-color: ${theme.colors.GlitterGrayL20};
          }
        `}
      `
    }

    ${
      hasError &&
      css`
        outline: 1px solid ${theme.colors.Danger} !important;
        border-color: ${theme.colors.Danger} !important;
      `
    }

    ${
      hasWarning &&
      css`
        outline: 1px solid ${theme.colors.Warning} !important;
        border-color: ${theme.colors.Warning} !important;
      `
    }
  `}
`;

const StyledInput = styled(BaseInput)<{
  isInGroup?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  isExpandable?: boolean;
  isExpanded?: boolean;
  hasBoxLabel?: boolean;
  isReadOnly?: boolean;
  mode?: Mode;
}>`
  ${({
    theme,
    isInGroup,
    isFirst,
    isLast,
    isExpandable,
    isExpanded,
    hasBoxLabel,
    isReadOnly,
    mode,
  }) => css`

    ${
      hasBoxLabel &&
      css`
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      `
    }

    ${
      isReadOnly &&
      css`
        pointer-events: none;
        background-color: ${theme.colors.GlitterGrayL20};
        color: ${theme.colors.OnyxGray};
      `
    }

    ${
      mode === 'Dark' &&
      css`
        ${darkStyles({ theme, isReadOnly })};
      `
    }

    ${
      isInGroup &&
      css`
        ${groupStyles({ isFirst, isLast, hasBoxLabel })};
      `
    }

    ${
      isExpandable &&
      css`
        ${expandableStyles({ isExpanded, hasBoxLabel })};
      `
    }
  `}
`;

const darkStyles = ({ theme, isReadOnly }: { theme: Theme; isReadOnly?: boolean }) => css`
  background-color: ${theme.colors.OnyxGrayD20};
  border-color: ${theme.colors.OnyxGrayL10};
  color: ${theme.colors.White};

  &::selection {
    background: ${theme.colors.OnyxGrayL10};
  }

  ${isReadOnly &&
  css`
    background-color: ${theme.colors.OnyxGray};
  `}
`;

const groupStyles = ({
  isFirst,
  isLast,
  hasBoxLabel,
}: {
  isFirst?: boolean;
  isLast?: boolean;
  hasBoxLabel?: boolean;
}) => css`
  ${
    isFirst &&
    css`
      border-bottom-right-radius: 0;
    `
  }
  ${
    isLast &&
    css`
      border-top-right-radius: 0;
    `
  }

  ${
    !isFirst &&
    !isLast &&
    css`
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    `
  }

  ${
    !hasBoxLabel &&
    css`
      ${
        isFirst &&
        css`
          border-bottom-left-radius: 0;
        `
      }
      ${
        isLast &&
        css`
          border-top-left-radius: 0;
        `
      }

      ${
        !isFirst &&
        !isLast &&
        css`
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
        `
      }
    `
  }
`;

const expandableStyles = ({
  isExpanded,
  hasBoxLabel,
}: {
  isExpanded?: boolean;
  hasBoxLabel?: boolean;
}) => css`
  ${isExpanded &&
  css`
    border-bottom-right-radius: 0;

    ${!hasBoxLabel &&
    css`
      border-bottom-left-radius: 0;
    `}
  `}
`;

export interface ValueInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  notInBox?: boolean;
  isReadOnly?: boolean;
  hasError?: boolean;
  hasWarning?: boolean;
}

export const ValueInput = React.forwardRef<HTMLInputElement>(
  ({ onBlur, onKeyDown, notInBox, isReadOnly, ...rest }: ValueInputProps, ref) => {
    return (
      <ValueBoxConsumer>
        {(contextValues) => (
          <EllipsisText disabled={!isReadOnly} content={rest.value}>
            {Object.keys(contextValues).length && !notInBox ? (
              <StyledInput
                {...contextValues}
                {...rest}
                isReadOnly={isReadOnly}
                grow
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                readOnly={isReadOnly}
                tabIndex={isReadOnly ? -1 : 0}
                ref={ref}
              />
            ) : (
              <BaseInput
                {...rest}
                readOnly={isReadOnly}
                grow
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                ref={ref}
              />
            )}
          </EllipsisText>
        )}
      </ValueBoxConsumer>
    );
  },
);
